import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Warehouse, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'Pickers & Packers',
  'Forklift Operators (Certified)',
  'Shipping & Receiving Clerks',
  'Inventory Management',
  'Loading Dock Workers',
  'Quality Control Staff',
  'Order Fulfilment Specialists',
  'Warehouse Supervisors',
];

const benefits = [
  'Experienced warehouse workers available 24-48 hours',
  'Forklift-certified operators on demand',
  'Flexible shifts: days, evenings, nights, weekends',
  'Full payroll and WCB administration',
  'Scalable workforce for peak seasons',
  'Pre-screened for reliability and safety',
];

const WarehousePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Warehouse & Fulfillment Staffing BC - WorkNation Labour Ltd | Warehouse Workers Vancouver Surrey</title>
        <meta name="description" content="WorkNation Labour Ltd provides warehouse and fulfillment centre staffing across BC. Pickers, packers, forklift operators, shipping/receiving. Fast placement, flexible shifts. Contact us today." />
        <link rel="canonical" href="https://worknation.ca/industries/warehouse" />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Warehouse className="text-blue-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-blue-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Warehouse & Fulfillment Centre Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd provides experienced warehouse and fulfillment centre workers to distribution hubs, e-commerce fulfillment operations, and logistics facilities across Metro Vancouver, Surrey, Richmond, Delta, and all of British Columbia. We keep your operations running with reliable, pre-screened staff.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="warehouse-cta">
              Get Warehouse Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Warehouse Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Warehouse Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              British Columbia&apos;s warehouse and logistics sector is one of the fastest-growing in Canada, driven by e-commerce growth and the Port of Vancouver\u2019s role as a major trade gateway. From massive distribution centres in Surrey and Delta to fulfillment hubs in Richmond, the demand for reliable warehouse workers has never been higher.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              WorkNation understands that warehouse operations run around the clock. We provide workers for all shifts\u2014days, evenings, nights, and weekends\u2014so your operation never misses a beat. Our forklift operators are fully certified, and all workers are safety-trained before assignment.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Seasonal surges? No problem. WorkNation scales your workforce up or down based on demand, so you only pay for the labour you need.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Warehouse Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default WarehousePage;
